/**
 * Parser simples de WKT (Well-Known Text) para MULTILINESTRING e MULTIPOLYGON.
 * Retorna coordenadas como arrays de [lon, lat].
 */

/**
 * Extrai pares de números (lon lat) de uma string como "( -7.96 37.01, -7.95 37.02 )"
 * @param {string} coordsStr
 * @returns {Array<[number, number]>}
 */
function parseCoordinateString(coordsStr) {
  const pairs = []
  const trimmed = coordsStr.trim()
  // Regex para pares "lon lat" (números com possível sinal e decimais)
  const regex = /(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g
  let m
  while ((m = regex.exec(trimmed)) !== null) {
    pairs.push([parseFloat(m[1]), parseFloat(m[2])])
  }
  return pairs
}

/**
 * Parse MULTILINESTRING ((lon lat, lon lat, ...), (lon lat, ...))
 * @param {string} wkt - ex: "MULTILINESTRING ((-7.96 37.01, -7.95 37.02))"
 * @returns {{ type: 'MULTILINESTRING', lines: Array<Array<[number, number]>> }}
 */
function parseMultiLineString(wkt) {
  const open = wkt.indexOf('((')
  if (open === -1) return { type: 'MULTILINESTRING', lines: [] }
  const inner = wkt.slice(open + 1, wkt.length - 1)
  const lines = []
  let depth = 0
  let start = 0
  for (let i = 0; i < inner.length; i++) {
    if (inner[i] === '(') {
      if (depth === 0) start = i + 1
      depth++
    } else if (inner[i] === ')') {
      depth--
      if (depth === 0) {
        const segment = inner.slice(start, i)
        const coords = parseCoordinateString(segment)
        if (coords.length > 0) lines.push(coords)
      }
    }
  }
  return { type: 'MULTILINESTRING', lines }
}

/**
 * Parse POLYGON ((lon lat, lon lat, ...))
 * @param {string} wkt
 * @returns {{ type: 'POLYGON', polygons: Array<Array<[number, number]>> }}
 */
function parsePolygon(wkt) {
  const open = wkt.indexOf('((')
  if (open === -1) return { type: 'POLYGON', polygons: [] }
  const close = wkt.lastIndexOf('))')
  if (close === -1) return { type: 'POLYGON', polygons: [] }
  const inner = wkt.slice(open + 2, close)
  const coords = parseCoordinateString(inner)
  return { type: 'POLYGON', polygons: coords.length > 0 ? [coords] : [] }
}

/**
 * Parse MULTIPOLYGON (((lon lat, ...)), (((lon lat, ...))))
 * Cada polígono pode ter anéis externo e buracos; usamos só o primeiro anel.
 * @param {string} wkt
 * @returns {{ type: 'MULTIPOLYGON', polygons: Array<Array<[number, number]>> }}
 */
function parseMultiPolygon(wkt) {
  const open = wkt.indexOf('(((')
  if (open === -1) return { type: 'MULTIPOLYGON', polygons: [] }
  const inner = wkt.slice(open + 1, wkt.length - 2)
  const polygons = []
  let depth = 0
  let start = 0
  for (let i = 0; i < inner.length; i++) {
    if (inner[i] === '(') {
      if (depth === 1) start = i + 1
      depth++
    } else if (inner[i] === ')') {
      depth--
      if (depth === 1) {
        const ring = inner.slice(start, i)
        const coords = parseCoordinateString(ring)
        if (coords.length > 0) polygons.push(coords)
      }
    }
  }
  return { type: 'MULTIPOLYGON', polygons }
}

/**
 * Parse WKT geometry (MULTILINESTRING ou MULTIPOLYGON).
 * @param {string} geometry - string WKT
 * @returns {{ type: string, lines?: Array<Array<[number, number]>>, polygons?: Array<Array<[number, number]>> }}
 */
export function parseWkt(geometry) {
  if (!geometry || typeof geometry !== 'string') {
    return { type: 'UNKNOWN' }
  }
  const g = geometry.trim().toUpperCase()
  if (g.startsWith('MULTILINESTRING')) {
    return parseMultiLineString(geometry)
  }
  if (g.startsWith('MULTIPOLYGON')) {
    return parseMultiPolygon(geometry)
  }
  if (g.startsWith('POLYGON')) {
    return parsePolygon(geometry)
  }
  return { type: 'UNKNOWN' }
}
